import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Receipt,
  Refresh,
  Save,
} from '@mui/icons-material';
import type { CustomerIndexItemDto, Vendor } from '@techgitter/contracts';
import { AliasType } from '@techgitter/contracts';
import { CustomerLookupControl } from '../components/tickets/Customer/CustomerLookupControl';
import { useCustomerAliases } from '../hooks/useCustomerAliases';
import { useCustomerServices } from '../hooks/useCustomerServices';
import { useRenewalSchedule } from '../hooks/useRenewalSchedule';
import {
  getVendorMappings,
  type VendorMappingRecord,
} from '../services/vendorMappingsService';

const REPAIRSHOPR_BASE_URL = 'https://techsolvers.repairshopr.com';

interface VendorMappingsPageProps {
  vendor: Vendor;
  onBack: () => void;
}

const openInvoices = (repairShoprCustomerId: string) => {
  window.open(
    `${REPAIRSHOPR_BASE_URL}/invoices?search%5Bcustomer_id%5D=${repairShoprCustomerId}`,
    '_blank',
    'noopener,noreferrer'
  );
};

export const VendorMappingsPage: React.FC<VendorMappingsPageProps> = ({
  vendor,
  onBack,
}) => {
  const [mappings, setMappings] = useState<VendorMappingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerIndexItemDto | null>(null);
  const [mappingValue, setMappingValue] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadMappings = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      const result = await getVendorMappings(vendor.vendorId, signal);
      if (signal?.aborted) return;
      setMappings(result.items);
      setWarnings(result.warnings);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load vendor mappings');
      setMappings([]);
      setWarnings([]);
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [vendor.vendorId]);

  useEffect(() => {
    const controller = new AbortController();
    loadMappings(controller.signal);
    return () => controller.abort();
  }, [loadMappings]);

  const {
    aliases: selectedCustomerAliases,
    isLoading: selectedCustomerAliasesLoading,
    error: selectedCustomerAliasesError,
    create: createAlias,
    update: updateAlias,
  } = useCustomerAliases(selectedCustomer?.dataverseCustomerId ?? null);

  const {
    services,
    isLoading: servicesLoading,
    error: servicesError,
  } = useCustomerServices(selectedCustomer?.dataverseCustomerId ?? null, 'all');

  const {
    schedule: renewalSchedule,
    isLoading: scheduleLoading,
    error: scheduleError,
  } = useRenewalSchedule(selectedCustomer?.dataverseCustomerId ?? null);

  const currentMapping = useMemo(
    () =>
      selectedCustomerAliases.find(
        (alias) =>
          alias.aliasType === AliasType.VendorMatching &&
          alias.vendorId === vendor.vendorId
      ) ?? null,
    [selectedCustomerAliases, vendor.vendorId]
  );

  useEffect(() => {
    setMappingValue(currentMapping?.alias ?? '');
  }, [currentMapping?.alias, currentMapping?.aliasId, selectedCustomer?.dataverseCustomerId]);

  useEffect(() => {
    setSaveError(null);
    setSaveSuccess(null);
  }, [selectedCustomer?.dataverseCustomerId]);

  const filteredMappings = useMemo(() => {
    if (!tableSearch.trim()) {
      return mappings;
    }

    const term = tableSearch.trim().toLowerCase();
    return mappings.filter((mapping) =>
      mapping.customer.displayName.toLowerCase().includes(term) ||
      mapping.alias.alias.toLowerCase().includes(term) ||
      mapping.alias.aliasNormalized.toLowerCase().includes(term) ||
      mapping.customer.domains?.toLowerCase().includes(term)
    );
  }, [mappings, tableSearch]);

  const monthlyScheduleItems = useMemo(
    () => renewalSchedule.filter((item) => item.frequency.toLowerCase().includes('month')),
    [renewalSchedule]
  );

  const annualScheduleItems = useMemo(
    () => renewalSchedule.filter((item) => item.frequency.toLowerCase().includes('annual')),
    [renewalSchedule]
  );

  const trimmedMappingValue = mappingValue.trim();
  const hasPendingChange = Boolean(
    selectedCustomer &&
    trimmedMappingValue &&
    (
      !currentMapping ||
      trimmedMappingValue !== currentMapping.alias.trim() ||
      !currentMapping.isActive
    )
  );

  const handleSelectCustomer = (customer: CustomerIndexItemDto | null) => {
    setSelectedCustomer(customer);
  };

  const handleRefresh = () => {
    loadMappings();
  };

  const handleSave = async () => {
    if (!selectedCustomer || !trimmedMappingValue) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    try {
      if (currentMapping) {
        await updateAlias(currentMapping.aliasId, {
          alias: trimmedMappingValue,
          aliasType: AliasType.VendorMatching,
          vendorId: vendor.vendorId,
          isActive: true,
        });
      } else {
        await createAlias({
          alias: trimmedMappingValue,
          aliasType: AliasType.VendorMatching,
          vendorId: vendor.vendorId,
          isActive: true,
        });
      }

      await loadMappings();
      setSaveSuccess(currentMapping ? 'Mapping updated.' : 'Mapping created.');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save mapping');
    } finally {
      setIsSaving(false);
    }
  };

  const openScheduleGroup = (scheduleIds: string[]) => {
    scheduleIds.forEach((scheduleId) => {
      window.open(
        `${REPAIRSHOPR_BASE_URL}/schedules/${scheduleId}/edit`,
        '_blank',
        'noopener,noreferrer'
      );
    });
  };

  const selectedCustomerInfo = selectedCustomer
    ? [
        { label: 'Type', value: selectedCustomer.customerType },
        { label: 'Designation', value: selectedCustomer.designation },
        { label: 'Email', value: selectedCustomer.email },
        { label: 'Phone', value: selectedCustomer.phone || selectedCustomer.mobile },
        { label: 'Domains', value: selectedCustomer.domains },
      ].filter((field) => field.value)
    : [];

  return (
    <Box sx={{ padding: 3, paddingBottom: 8 }}>
      <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>
        Back to Vendors
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4">{vendor.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            Vendor mappings for {vendor.code}
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={handleRefresh} disabled={loading}>
          Refresh
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Add or Update Mapping</Typography>
          <CustomerLookupControl
            value={selectedCustomer}
            onChange={handleSelectCustomer}
          />

          {selectedCustomer && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                }}
              >
                {selectedCustomerInfo.map((field) => (
                  <Box key={field.label}>
                    <Typography variant="caption" color="text.secondary">
                      {field.label}
                    </Typography>
                    <Typography variant="body2">{field.value}</Typography>
                  </Box>
                ))}
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Services
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {servicesLoading && <CircularProgress size={18} />}
                  {!servicesLoading && services.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No services found.
                    </Typography>
                  )}
                  {services.map((service) => (
                    <Chip
                      key={service.customerServiceId}
                      label={`${service.displayName} - ${service.status.label}`}
                      variant="outlined"
                      sx={{
                        borderColor: service.status.colour,
                        color: service.status.colour,
                      }}
                    />
                  ))}
                </Box>
                {servicesError && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {servicesError}
                  </Alert>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<Receipt />}
                  onClick={() => openInvoices(selectedCustomer.repairShoprCustomerId)}
                >
                  All Invoices
                </Button>
                <Button
                  variant="outlined"
                  disabled={monthlyScheduleItems.length === 0 || scheduleLoading}
                  onClick={() => openScheduleGroup(monthlyScheduleItems.map((item) => item.repairShoprScheduleId))}
                >
                  Monthly Billing
                </Button>
                <Button
                  variant="outlined"
                  disabled={annualScheduleItems.length === 0 || scheduleLoading}
                  onClick={() => openScheduleGroup(annualScheduleItems.map((item) => item.repairShoprScheduleId))}
                >
                  Annual Billing
                </Button>
              </Box>
              {scheduleError && (
                <Alert severity="warning">{scheduleError}</Alert>
              )}

              {selectedCustomerAliasesError && (
                <Alert severity="error">{selectedCustomerAliasesError}</Alert>
              )}

              <TextField
                label="Mapping"
                value={mappingValue}
                onChange={(event) => setMappingValue(event.target.value)}
                placeholder="Enter the vendor-facing customer name"
                fullWidth
                disabled={selectedCustomerAliasesLoading}
                helperText={
                  currentMapping
                    ? currentMapping.isActive
                      ? 'Saving will update the existing mapping for this customer.'
                      : 'Saving will reactivate this mapping for the customer.'
                    : 'Saving will create a new vendor mapping for this customer.'
                }
              />

              {saveError && <Alert severity="error">{saveError}</Alert>}
              {saveSuccess && <Alert severity="success">{saveSuccess}</Alert>}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSave}
                  disabled={
                    !hasPendingChange ||
                    isSaving ||
                    selectedCustomerAliasesLoading ||
                    Boolean(selectedCustomerAliasesError)
                  }
                >
                  {isSaving ? 'Saving...' : currentMapping ? 'Save Changes' : 'Save Mapping'}
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </Paper>

      {warnings.map((warning) => (
        <Alert key={warning} severity="warning" sx={{ mb: 2 }}>
          {warning}
        </Alert>
      ))}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Current Mappings</Typography>
          <TextField
            size="small"
            placeholder="Search mappings..."
            value={tableSearch}
            onChange={(event) => setTableSearch(event.target.value)}
            sx={{ maxWidth: 320 }}
          />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredMappings.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {mappings.length === 0
                ? 'No mappings exist for this vendor yet.'
                : 'No mappings match the current search.'}
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell>Domains</TableCell>
                    <TableCell>Mapping</TableCell>
                    <TableCell>Normalised</TableCell>
                    <TableCell align="center">Active</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMappings.map((mapping) => {
                    const isSelected =
                      selectedCustomer?.dataverseCustomerId === mapping.customer.dataverseCustomerId;

                    return (
                      <TableRow
                        key={mapping.alias.aliasId}
                        hover
                        selected={isSelected}
                        onClick={() => setSelectedCustomer(mapping.customer)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {mapping.customer.displayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {mapping.customer.customerType}
                          </Typography>
                        </TableCell>
                        <TableCell>{mapping.customer.domains || '—'}</TableCell>
                        <TableCell>{mapping.alias.alias}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {mapping.alias.aliasNormalized || '—'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={mapping.alias.isActive ? 'Active' : 'Inactive'}
                            color={mapping.alias.isActive ? 'success' : 'default'}
                            variant={mapping.alias.isActive ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};
